import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useIntl } from '@umijs/max';
import * as Icons from '@ant-design/icons';
import { DownOutlined } from "@ant-design/icons";
import {
  AvatarDropdown,
  AvatarName,
  Footer,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { Image } from 'antd';
import NoAccessible from './pages/403';
import { currentUser } from './services/user/api';

// 检查路径是否在允许的菜单中
const isAllowedPath = (pathname: string, menus?: any[]): boolean => {
  if (!menus) return false;
  const checkMenu = (menus: any[]): boolean => {
    for (const menu of menus) {
      if (menu.uri === pathname) {
        return true;
      }
      if (menu.children && menu.children.length > 0) {
        if (checkMenu(menu.children)) {
          return true;
        }
      }
    }
    return false;
  };
  return checkMenu(menus);
};

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * 用于设置全局返回状态，可以在全局使用
 * const { initialState, loading, error, refresh, setInitialState } = useModel("@@initialState");
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  region?: Record<string, string>;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menuData?: any[];
}> {
  const fetchUserInfo = async () => {
    try {
      const response = await currentUser();
      console.log(response);
      return response.data;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![
      loginPath,
      "/user/password-result",
      "/user/reset-password",
      "/user/update-password",
      "/user/forgot-password",
    ].includes(location.pathname)
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const intl = useIntl();
  return {
    title: false,
    logo: () => <Image src="/assets/menu-icon.webp" preview={false} />,
    //-- 自定义 logo 和 title
    headerTitleRender: (logo, title) => {
      return (
        <>
          {logo}
        </>
      );
    },
    //-- 顶部右侧 语言切换
    actionsRender: () => [
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      gap: 0,
      src: <AvatarName />,
      title: <DownOutlined style={{ fontSize: 10 }} />,
      render: (_, avatarChildren) => {
        return (
          <>
            <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>
          </>
        );
      },
    },
    //-- 配置水印，会在页面显示水印
    waterMarkProps: {
      content: undefined,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      } 
      else if (initialState?.currentUser && !isAllowedPath(location.pathname, initialState?.currentUser.menus)) {
        // 如果登录但无权限，跳转到 403 页面
        history.push('/403');
      }
    },
    menu: {
      locale: false, // 关闭菜单国际化
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    //-- 左侧底部链接
    links: isDev
      ? [
          // <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          //   <LinkOutlined />
          //   <span>OpenAPI 文档</span>
          // </Link>,
        ]
      : [],
    //-- 内容区样式
    contentStyle: {
      padding: 16,
    },
    // 基于后端 menuData 构建菜单项
    menuDataRender: () => {
      const transformMenu = (menus?: any[]): any[] => {
        if (!menus || !Array.isArray(menus)) {
          return [];
        }
        return menus.map((menu) => {
          const IconComponent = menu.icon
            ? (Icons[menu.icon as keyof typeof Icons] as React.ComponentType)
            : undefined;
          return {
            key: menu.id,
            name: menu.title,
            path: menu.uri,
            icon: IconComponent ? <IconComponent /> : undefined,
            // 保留路由层级用 children
            children: transformMenu(menu.children),
            // 如果需要做内置权限过滤可放在 accessKey
            accessKey: menu.permission_name || undefined,
            access: menu.permission_name
              ? `hasPermission:${menu.permission_name}`
              : undefined,
          };
        });
      };
      return transformMenu(initialState?.currentUser?.menus || []);
    },
    // 自定义menu 顶部渲染Render
    menuHeaderRender: undefined,
    // 自定义 403 页面
    unAccessible: <NoAccessible />,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  //-- 使用绝对路径会导致 proxy 配置无效
  baseURL: '/',
  ...errorConfig,
};
